<template>
  <div class="diagnosis-combobox" ref="rootRef">
    <input
      :id="inputId"
      type="text"
      class="diagnosis-combobox-input"
      :class="inputClass"
      :value="modelValue"
      :placeholder="placeholder"
      :required="required"
      :aria-required="required ? 'true' : undefined"
      autocomplete="off"
      role="combobox"
      :aria-expanded="showDropdown"
      aria-autocomplete="list"
      @input="onInput"
      @focus="onFocus"
      @keydown.down.prevent="highlightNext"
      @keydown.up.prevent="highlightPrev"
      @keydown.enter.prevent="selectHighlighted"
      @keydown.escape="closeDropdown"
    />
    <ul
      v-if="showDropdown && dropdownOptions.length"
      class="diagnosis-combobox-list"
      role="listbox"
    >
      <li
        v-if="patientSectionCount"
        class="diagnosis-combobox-section"
        aria-hidden="true"
      >
        Patient diagnoses
      </li>
      <li
        v-for="(option, index) in dropdownOptions"
        :key="option.key"
        class="diagnosis-combobox-item"
        :class="{
          'diagnosis-combobox-item--patient': option.source === 'patient',
          'diagnosis-combobox-item--active': index === highlightedIndex,
        }"
        role="option"
        @mousedown.prevent="selectOption(option)"
      >
        <strong class="diagnosis-combobox-title">{{ option.label }}</strong>
        <small v-if="option.code" class="diagnosis-combobox-code">{{ option.code }}</small>
      </li>
    </ul>
  </div>
</template>

<script>
import axios from "axios";
import { CURR_API } from "@/constants/apis";

function parseDiagnosisLabel(text) {
  const raw = (text || "").trim();
  if (!raw) return { code: "", description: "", label: "" };
  const dashIdx = raw.indexOf(" - ");
  if (dashIdx > 0) {
    return {
      code: raw.slice(0, dashIdx).trim(),
      description: raw.slice(dashIdx + 3).trim(),
      label: raw,
    };
  }
  return { code: raw, description: raw, label: raw };
}

function formatDiagnosisLabel(code, description) {
  const c = (code || "").trim();
  const d = (description || "").trim();
  if (c && d && d !== c) return `${c} - ${d}`;
  return c || d;
}

export { parseDiagnosisLabel, formatDiagnosisLabel };

export default {
  name: "DiagnosisCombobox",
  props: {
    modelValue: { type: String, default: "" },
    patientId: { type: String, default: "" },
    extraDiagnoses: { type: Array, default: () => [] },
    inputId: { type: String, default: "" },
    inputClass: { type: [String, Array, Object], default: "" },
    placeholder: { type: String, default: "Diagnosis" },
    required: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "select"],
  data() {
    return {
      patientDiags: [],
      searchResults: [],
      showDropdown: false,
      highlightedIndex: -1,
      searchTimer: null,
      loadToken: 0,
    };
  },
  computed: {
    patientOptions() {
      const seen = new Set();
      const options = [];
      const addOption = (code, description, label, source, keyPrefix) => {
        const parsed = parseDiagnosisLabel(label || formatDiagnosisLabel(code, description));
        const finalCode = (code || parsed.code || "").trim();
        const finalDesc = (description || parsed.description || "").trim();
        const finalLabel = label || formatDiagnosisLabel(finalCode, finalDesc);
        const dedupeKey = `${finalCode}|${finalDesc}`.toLowerCase();
        if (!finalLabel || seen.has(dedupeKey)) return;
        seen.add(dedupeKey);
        options.push({
          key: `${keyPrefix}-${dedupeKey}`,
          code: finalCode,
          description: finalDesc,
          label: finalLabel,
          source,
        });
      };

      (this.patientDiags || []).forEach((row, index) => {
        const parsed = parseDiagnosisLabel(row.diagnosis_code);
        addOption(
          parsed.code,
          parsed.description,
          parsed.label,
          "patient",
          `patient-${index}`
        );
      });

      (this.extraDiagnoses || []).forEach((row, index) => {
        addOption(
          row.code,
          row.description,
          row.label || row.description,
          "patient",
          `extra-${index}`
        );
      });

      return options;
    },
    filteredPatientOptions() {
      const q = (this.modelValue || "").trim().toLowerCase();
      if (!q) return this.patientOptions;
      return this.patientOptions.filter(
        (opt) =>
          opt.label.toLowerCase().includes(q) ||
          opt.code.toLowerCase().includes(q) ||
          opt.description.toLowerCase().includes(q)
      );
    },
    searchOptions() {
      const patientKeys = new Set(
        this.patientOptions.map((opt) => `${opt.code}|${opt.description}`.toLowerCase())
      );
      return (this.searchResults || [])
        .map((row, index) => {
          const label = formatDiagnosisLabel(row.code, row.description);
          const key = `${row.code}|${row.description}`.toLowerCase();
          if (patientKeys.has(key)) return null;
          return {
            key: `search-${index}-${key}`,
            code: row.code,
            description: row.description,
            label,
            source: "search",
          };
        })
        .filter(Boolean);
    },
    dropdownOptions() {
      const patientMatches = this.filteredPatientOptions;
      const searchMatches =
        (this.modelValue || "").trim().length > 0 ? this.searchOptions : [];
      return [...patientMatches, ...searchMatches];
    },
    patientSectionCount() {
      return this.dropdownOptions.filter((opt) => opt.source === "patient").length;
    },
  },
  watch: {
    patientId: {
      immediate: true,
      handler() {
        this.loadPatientDiagnoses();
      },
    },
    extraDiagnoses: {
      deep: true,
      handler() {
        if (this.showDropdown) this.highlightedIndex = 0;
      },
    },
  },
  mounted() {
    document.addEventListener("click", this.onDocumentClick);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.onDocumentClick);
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },
  methods: {
    async loadPatientDiagnoses() {
      const patientID = String(this.patientId ?? "").trim();
      if (!patientID) {
        this.patientDiags = [];
        return;
      }
      const token = ++this.loadToken;
      try {
        const res = await axios.post(
          CURR_API + "/tswebhook.php",
          {
            Diagnosis: {
              API_Meth: "PopulateDiagGrid",
              patientID,
            },
          },
          { headers: { "Content-Type": "application/json;" } }
        );
        if (token !== this.loadToken) return;
        if (res.data?.gridhtml && Array.isArray(res.data.data)) {
          this.patientDiags = res.data.data.filter(
            (diag) => diag.diagnosis_type === "Diagnosis"
          );
        } else {
          this.patientDiags = [];
        }
      } catch (err) {
        console.error("Failed to load patient diagnoses:", err);
        if (token === this.loadToken) this.patientDiags = [];
      }
    },
    onInput(event) {
      const value = event.target.value;
      this.$emit("update:modelValue", value);
      this.showDropdown = true;
      this.highlightedIndex = 0;
      this.scheduleSearch(value);
    },
    onFocus() {
      this.showDropdown = true;
      if (this.highlightedIndex < 0 && this.dropdownOptions.length) {
        this.highlightedIndex = 0;
      }
      if ((this.modelValue || "").trim()) {
        this.scheduleSearch(this.modelValue);
      }
    },
    onDocumentClick(event) {
      if (!this.$refs.rootRef?.contains(event.target)) {
        this.closeDropdown();
      }
    },
    scheduleSearch(term) {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      const q = (term || "").trim();
      if (!q) {
        this.searchResults = [];
        return;
      }
      this.searchTimer = setTimeout(() => this.fetchDiagnosisSearch(q), 250);
    },
    async fetchDiagnosisSearch(term) {
      try {
        const res = await axios.post(
          CURR_API + "/icd_calls.php",
          {
            action: "CodeLookUp",
            searchTerm: term,
          },
          { headers: { "Content-Type": "application/json;" } }
        );
        if (!res.data || res.data.result <= 0) {
          this.searchResults = [];
        } else {
          this.searchResults = res.data.actualResults || [];
        }
      } catch (err) {
        console.error("Diagnosis search failed:", err);
        this.searchResults = [];
      }
    },
    selectOption(option) {
      this.$emit("update:modelValue", option.label);
      this.$emit("select", {
        code: option.code,
        description: option.description,
        source: option.source,
      });
      this.closeDropdown();
    },
    selectHighlighted() {
      if (!this.showDropdown || !this.dropdownOptions.length) return;
      const index = this.highlightedIndex >= 0 ? this.highlightedIndex : 0;
      const option = this.dropdownOptions[index];
      if (option) this.selectOption(option);
    },
    highlightNext() {
      if (!this.dropdownOptions.length) return;
      this.showDropdown = true;
      this.highlightedIndex = Math.min(
        this.highlightedIndex + 1,
        this.dropdownOptions.length - 1
      );
    },
    highlightPrev() {
      if (!this.dropdownOptions.length) return;
      this.showDropdown = true;
      this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
    },
    closeDropdown() {
      this.showDropdown = false;
      this.highlightedIndex = -1;
    },
  },
};
</script>

<style scoped lang="scss">
.diagnosis-combobox {
  position: relative;
  display: block;
  width: 100%;
}

/* Match sibling .form-group / .mobile-form-group inputs (parent scoped CSS does not pierce). */
.diagnosis-combobox-input {
  display: block;
  width: 100%;
  box-sizing: border-box;
  margin: 0;
  padding: 0.4rem 0.6rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font: inherit;
  font-size: inherit;
  line-height: normal;
  color: inherit;
  background-color: #fff;
  -webkit-appearance: none;
  appearance: none;
}

.diagnosis-combobox-input.mobile-input {
  padding: 0.6rem;
  font-size: 1rem;
}

.diagnosis-combobox-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  max-height: 220px;
  overflow-y: auto;
  margin: 0;
  padding: 0;
  list-style: none;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);
}

.diagnosis-combobox-section {
  padding: 6px 12px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.diagnosis-combobox-item {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #eee;
}

.diagnosis-combobox-item:last-child {
  border-bottom: none;
}

.diagnosis-combobox-item--patient {
  background: #f8fafc;
}

.diagnosis-combobox-item--active,
.diagnosis-combobox-item:hover {
  background: #e6f4f4;
}

.diagnosis-combobox-title {
  display: block;
  font-size: 0.92rem;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.35;
}

.diagnosis-combobox-code {
  display: block;
  margin-top: 2px;
  font-size: 0.8rem;
  color: #64748b;
}
</style>
